import React from 'react';
import { useLocation } from 'wouter';
import { Layout } from '@/components/layout';
import { Button, CompactSelect } from '@/components/ui-elements';
import { useAuthStore } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listSessions,
  createSession,
  deleteSession,
  type ReconciliationSession,
} from '@/lib/reconcile-api';
import { fmtDate } from '@/lib/dateFormat';
import { Scale, Plus, Trash2, ArrowRight } from 'lucide-react';

const QUARTERS: { label: string; start: string; end: string }[] = (() => {
  const year = new Date().getFullYear();
  return [
    { label: `Q1 ${year}`, start: `${year}-01-01`, end: `${year}-03-31` },
    { label: `Q2 ${year}`, start: `${year}-04-01`, end: `${year}-06-30` },
    { label: `Q3 ${year}`, start: `${year}-07-01`, end: `${year}-09-30` },
    { label: `Q4 ${year}`, start: `${year}-10-01`, end: `${year}-12-31` },
    { label: `Q1 ${year - 1}`, start: `${year - 1}-01-01`, end: `${year - 1}-03-31` },
    { label: `Q2 ${year - 1}`, start: `${year - 1}-04-01`, end: `${year - 1}-06-30` },
    { label: `Q3 ${year - 1}`, start: `${year - 1}-07-01`, end: `${year - 1}-09-30` },
    { label: `Q4 ${year - 1}`, start: `${year - 1}-10-01`, end: `${year - 1}-12-31` },
  ];
})();

function statusBadge(status: ReconciliationSession['status']) {
  if (status === 'done')
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
        Done
      </span>
    );
  if (status === 'running')
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
        Running
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-bold bg-slate-500/10 text-muted-foreground border border-slate-500/20">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
      Pending
    </span>
  );
}

export function Reconciliation() {
  const token = useAuthStore((s) => s.token);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showNew, setShowNew] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [periodMode, setPeriodMode] = React.useState<'quarter' | 'custom'>('quarter');
  const [selectedQuarter, setSelectedQuarter] = React.useState(QUARTERS[0]!.label);
  const [customStart, setCustomStart] = React.useState('');
  const [customEnd, setCustomEnd] = React.useState('');

  const { data: sessions, isLoading } = useQuery({
    queryKey: ['reconciliation-sessions'],
    queryFn: () => listSessions(token),
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; periodStart: string; periodEnd: string }) =>
      createSession(token, data),
    onSuccess: (session) => {
      queryClient.invalidateQueries({ queryKey: ['reconciliation-sessions'] });
      setShowNew(false);
      setNewName('');
      setLocation(`/reconciliation/${session.id}`);
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSession(token, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reconciliation-sessions'] });
      toast({ title: 'Session deleted' });
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    let start: string, end: string;
    if (periodMode === 'quarter') {
      const q = QUARTERS.find((q) => q.label === selectedQuarter)!;
      start = q.start;
      end = q.end;
    } else {
      start = customStart;
      end = customEnd;
    }
    createMutation.mutate({ name: newName.trim(), periodStart: start, periodEnd: end });
  }

  return (
    <Layout>
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display text-gradient mb-2">Reconciliation</h1>
          <p className="text-muted-foreground">
            Match Shopify payouts against gateway statements and generate Reckon summaries.
          </p>
        </div>
        <Button onClick={() => setShowNew(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Session
        </Button>
      </div>

      {showNew && (
        <div className="glass-panel rounded-2xl border border-border p-6 mb-8">
          <h2 className="text-lg font-display font-semibold mb-4">Create Reconciliation Session</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
                Session Name
              </label>
              <input
                type="text"
                placeholder="e.g. Q1 2026 Reconciliation"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="w-full h-10 px-3 bg-muted/50 border border-border rounded-xl text-sm focus:outline-none focus:border-primary/50 text-foreground placeholder:text-slate-600"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
                Period
              </label>
              <div className="flex gap-3 mb-3">
                <button
                  type="button"
                  onClick={() => setPeriodMode('quarter')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    periodMode === 'quarter'
                      ? 'bg-primary/20 text-primary border border-primary/30'
                      : 'bg-muted/50 border border-border text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Quarterly
                </button>
                <button
                  type="button"
                  onClick={() => setPeriodMode('custom')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    periodMode === 'custom'
                      ? 'bg-primary/20 text-primary border border-primary/30'
                      : 'bg-muted/50 border border-border text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Custom Range
                </button>
              </div>

              {periodMode === 'quarter' ? (
                <CompactSelect
                  value={selectedQuarter}
                  onChange={(e) => setSelectedQuarter(e.target.value)}
                  className="w-full h-10"
                >
                  {QUARTERS.map((q) => (
                    <option key={q.label} value={q.label}>
                      {q.label} ({q.start} – {q.end})
                    </option>
                  ))}
                </CompactSelect>
              ) : (
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs text-muted-foreground mb-1">Start Date</label>
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      className="w-full h-10 px-3 bg-muted/50 border border-border rounded-xl text-sm focus:outline-none focus:border-primary/50 text-foreground"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-muted-foreground mb-1">End Date</label>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      className="w-full h-10 px-3 bg-muted/50 border border-border rounded-xl text-sm focus:outline-none focus:border-primary/50 text-foreground"
                      required
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create Session'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowNew(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="glass-panel rounded-2xl border border-border/50 overflow-hidden">
        {isLoading ? (
          <div className="p-8 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-muted/50 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : sessions?.length === 0 ? (
          <div className="py-16 text-center">
            <Scale className="w-10 h-10 mx-auto mb-4 opacity-20" />
            <p className="text-muted-foreground">No reconciliation sessions yet.</p>
            <p className="text-slate-600 text-sm mt-1">Create one to get started.</p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="uppercase tracking-wider text-[10px] font-bold text-muted-foreground bg-muted border-b border-border/50">
              <tr>
                <th className="px-6 py-4">Session Name</th>
                <th className="px-6 py-4">Period</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Created</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {sessions?.map((session) => (
                <tr key={session.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-6 py-4 font-medium text-foreground">{session.name}</td>
                  <td className="px-6 py-4 text-muted-foreground font-mono text-xs">
                    {fmtDate(session.periodStart)} –{' '}
                    {fmtDate(session.periodEnd)}
                  </td>
                  <td className="px-6 py-4">{statusBadge(session.status)}</td>
                  <td className="px-6 py-4 text-muted-foreground font-mono text-xs">
                    {fmtDate(session.createdAt)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => setLocation(`/reconciliation/${session.id}`)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
                      >
                        Open <ArrowRight className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Delete this session?')) deleteMutation.mutate(session.id);
                        }}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
